<nav
  class="fixed inset-x-0 top-0 bg-white/80 backdrop-blur-md border-b border-gray-200/50 h-14 z-50 shadow-sm transition-all">
  <div #navbarContainer class="mx-auto max-w-10xl px-4 sm:px-6 lg:px-8 h-full">
    <div class="relative flex items-center justify-between h-full">
      <!-- The identity group: logo, the phone-width context chip, and the desktop tab strip.
           It steps aside entirely while the phone search field is expanded (#602) — a 390 px bar cannot hold a
           logo, a tenant name and a search field at once, and the field is the thing that was asked for.
           `gap-4` rather than `space-x-4` throughout this bar: `space-x-*` is a margin on every DOM child but
           the first, so a child that renders nothing still collects one — a hole where a control is not. `gap`
           is between the boxes that actually exist. -->
      <div [ngClass]="{'lg:-ml-48 lg:mr-48': focus}" [class.hidden]="searchExpandedOnPhone()"
           class="flex min-w-0 items-center gap-4 transition-all xl:!ml-0 xl:!mr-0">
        <div [routerLink]="['/']" class="flex-shrink-0 flex items-center h-10 select-none cursor-pointer group">
          <div class="relative">
            <img alt="N5CM"
                 class="h-8 w-auto rounded-lg shadow-sm border border-gray-100 transition-transform group-hover:scale-105"
                 src="./assets/images/icon.png">
            <div class="absolute inset-0 rounded-lg shadow-inner pointer-events-none"></div>
          </div>
        </div>
        <div class="hidden lg:flex items-center">
          <div #tabsContainer class="flex items-center space-x-1 overflow-x-auto no-scrollbar">
            <app-navbar-office-item (click)="select(navbarItem)" *ngFor="let navbarItem of visibleItems"
                                    [navbarItem]="navbarItem"></app-navbar-office-item>
          </div>

          <!-- More Dropdown -->
          <div #dropdown *ngIf="overflowItems.length > 0" class="relative ml-1">
            <button (click)="moreVisible = !moreVisible"
                    [ngClass]="moreVisible ? 'bg-gray-100 text-gray-900' : 'text-gray-500 hover:bg-gray-100/80 hover:text-gray-900'"
                    class="flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200 h-9">
              <span>Mehr</span>
              <svg [ngClass]="{'rotate-180': moreVisible}" class="ml-1 h-4 w-4 transition-transform duration-200"
                   fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path d="M19 9l-7 7-7-7" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"/>
              </svg>
            </button>

            <div *ngIf="moreVisible"
                 class="absolute left-0 mt-2 w-48 rounded-xl bg-white/95 backdrop-blur-xl border border-gray-200/50 shadow-2xl py-1 z-50 animate-in fade-in slide-in-from-top-2 duration-200">
              <a (click)="select(item); moreVisible = false" *ngFor="let item of overflowItems"
                 class="block px-4 py-2.5 text-sm text-gray-600 hover:bg-theme-primary-50 hover:text-theme-primary-700 transition-colors cursor-pointer first:rounded-t-xl last:rounded-b-xl">
                {{ item.title }}
              </a>
            </div>
          </div>
        </div>
      </div>

      <!-- The context switcher's phone shape (#602), CENTRED in the bar.
           Absolutely positioned rather than placed in the flex row, and that is the whole point: the bar's two
           ends are a logo on the left and two icon buttons on the right, and they are not the same width — so a
           chip laid out between them lands wherever those two happen to leave room, which is next to the logo
           rather than in the middle. `left-1/2 -translate-x-1/2` centres it on the BAR, independently of what
           sits at either end, and the max width keeps it from ever reaching the icons (the name truncates
           instead). That cap is a PERCENTAGE, not `vw`: this box's containing block is the bar, so a percentage
           tracks the thing it must not collide with, while `vw` would keep describing the window even where
           the bar is narrower than it. It steps aside with the rest when the phone search is expanded.
           A full-width flex overlay with `pointer-events-none`, NOT `left-1/2 -translate-x-1/2`. The transform
           is the trap: a transformed element becomes the containing block for every `position: fixed`
           DESCENDANT, and the chip's drawer is fixed — it would have been positioned against this little box
           instead of the viewport, so an edge-to-edge drawer would have come out chip-sized. Flex centring
           needs no transform. `pointer-events-none` keeps the overlay from swallowing clicks meant for the
           logo and the icons underneath it; the inner box turns them back on for the chip and its drawer.
           The element renders itself only below Tailwind's `sm`; from `sm` upwards the floating pill in
           `app.component.html` is the one on screen and nothing here is in the DOM at all.
           No `sm:hidden` on this wrapper, deliberately: the breakpoint decision belongs to
           `ViewportService.isBelowSm()` inside the component, and a class here would be a SECOND source of
           truth for the same fact — the pair can disagree, and the one that wins is whichever is more
           specific rather than whichever is right. Above `sm` this box simply has no child and collapses to
           0x0: invisible, unclickable, and not in anybody's way. -->
      <div [class.hidden]="searchExpandedOnPhone()"
           class="pointer-events-none absolute inset-0 z-[1] flex items-center justify-center">
        <div class="pointer-events-auto max-w-[46%]">
          <auth-context-switcher placement="header"/>
        </div>
      </div>

      <!-- Phone only: the search collapses to its own icon and expands back into the full field on tap (#602).
           The chip needs the room the field used to take, and search is the one thing in this bar that can give
           it up without losing a destination — it is still one tap away, and ⌘K still opens it directly.
           A real <button> with a name and `aria-expanded`, not a bare icon: this is the only way into the field
           at this width, so it has to be reachable by keyboard and announce what it does. -->
      @if (searchCollapsed()) {
        <button type="button" (click)="expandSearch()" [attr.aria-expanded]="false"
                aria-controls="search-input" aria-label="Suche öffnen" data-test="search-toggle"
                class="ml-auto mr-1 p-2 text-gray-500 hover:bg-gray-100/80 rounded-lg transition-colors">
          <svg aria-hidden="true" class="h-6 w-6" fill="currentColor" viewBox="0 0 20 20">
            <path clip-rule="evenodd"
                  d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z"
                  fill-rule="evenodd"/>
          </svg>
        </button>
      }

      <!-- Kept in the DOM rather than removed while collapsed: `checkOverflow()` bails out entirely when
           `#searchContainer` is missing, so the desktop tab strip would stop reflowing. `hidden` is enough. -->
      <div #searchContainer [class.hidden]="searchCollapsed()"
           class="flex flex-1 justify-center lg:justify-end lg:focus-within:-ml-48 xl:!ml-6 transition-all px-4">
        <div class="w-full max-w-lg lg:max-w-xl group">
          <!--
            `for` must name the INNER input, not the `<ng-select>` host: a custom element is not labelable, so
            with `for="search"` the search field counted as unlabelled (axe `label`, critical). `labelForId`
            is ng-select's own hook for exactly this — it stamps the id onto the input it renders.
          -->
          <label class="sr-only" for="search-input">Suche</label>
          <div class="relative">
            <ng-select #searchElement (change)="navigate($event);searchElement.blur()" (focusin)="loadItems()"
                       (focusout)="focusout()"
                       [(ngModel)]="selected" [groupBy]="groupBy"
                       [items]="NavbarService.itemBases" [searchFn]="search"
                       bindLabel="value" bindValue="id"
                       class="apple-search-select"
                       id="search" labelForId="search-input" name="search" notFoundText="Keine Ergebnisse">
            </ng-select>
            <div class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
              <svg aria-hidden="true"
                   class="h-4 w-4 text-gray-400 group-focus-within:text-theme-primary-500 transition-colors"
                   fill="currentColor" viewBox="0 0 20 20">
                <path clip-rule="evenodd"
                      d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z"
                      fill-rule="evenodd"/>
              </svg>
            </div>
            <div class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 hidden lg:flex">
              <kbd
                class="hidden sm:inline-flex items-center px-1.5 font-sans text-[10px] font-medium text-gray-400 border border-gray-200 rounded-md bg-gray-50/50">
                <span class="text-xs">⌘</span>K
              </kbd>
            </div>
          </div>
        </div>
      </div>

      <!-- The way back out of the expanded phone search. Without it the only exit would be a navigation, and
           the logo and the context chip stay hidden behind the field until the bar is put back. -->
      @if (searchExpandedOnPhone()) {
        <button type="button" (click)="collapseSearch()" aria-label="Suche schließen"
                data-test="search-close"
                class="mr-1 p-2 text-gray-500 hover:bg-gray-100/80 rounded-lg transition-colors">
          <svg aria-hidden="true" class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path d="M6 18L18 6M6 6l12 12" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"/>
          </svg>
        </button>
      }

      <div class="flex items-center space-x-2">
        <div class="flex lg:hidden">
          <!--
            `aria-controls` names the overlay below (which only exists while open) and `aria-expanded` has to
            track `menuVisible` — it used to be hard-coded "false", so a screen reader announced the menu as
            collapsed while it was on screen, and the referenced id existed nowhere in the document.
          -->
          <button (click)="menuToggle()" [attr.aria-expanded]="menuVisible" aria-controls="mobile-menu"
                  aria-label="Menü"
                  class="p-2 text-gray-500 hover:bg-gray-100/80 rounded-lg transition-colors"
                  type="button">
            <svg *ngIf="!menuVisible" class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path d="M4 6h16M4 12h16m-7 6h7" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"/>
            </svg>
            <svg *ngIf="menuVisible" class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path d="M6 18L18 6M6 6l12 12" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"/>
            </svg>
          </button>
        </div>

        <div class="hidden lg:flex items-center space-x-3">
          <button (click)="select(NavbarService.getHelpItem())"
                  class="p-2 text-gray-500 hover:bg-gray-100/80 rounded-lg transition-colors group"
                  title="Hilfe">
            <svg class="h-6 w-6 transition-transform group-hover:scale-110" fill="none" stroke="currentColor"
                 viewBox="0 0 256 256">
              <path d="M140 180a12 12 0 1 1-12-12a12 12 0 0 1 12 12M128 72c-22.06 0-40 16.15-40
              36v4a8 8 0 0 0 16 0v-4c0-11 10.77-20 24-20s24 9 24 20s-10.77 20-24 20a8 8 0 0 0-8 8v8a8 8 0 0 0 16
              0v-.72c18.24-3.35 32-17.9 32-35.28c0-19.85-17.94-36-40-36m104 56A104 104 0 1 1 128 24a104.11 104.11 0 0 1
              104 104m-16 0a88 88 0 1 0-88 88a88.1 88.1 0 0 0 88-88" fill="currentColor"/>
            </svg>
          </button>

          <div class="h-6 w-px bg-gray-200 mx-1"></div>

          <button (click)="select(NavbarService.userNavbar)"
                  class="flex items-center space-x-2 p-1 pl-1 pr-3 rounded-full hover:bg-gray-100/80 transition-colors group">
            <div
              class="h-8 w-8 rounded-full bg-gradient-to-br from-gray-100 to-gray-200 border border-gray-200 flex items-center justify-center overflow-hidden shadow-sm">
              <svg class="h-5 w-5 text-gray-500 group-hover:text-gray-700" fill="currentColor" viewBox="0 0 20 20">
                <path clip-rule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" fill-rule="evenodd"/>
              </svg>
            </div>
            <span
              class="text-xs font-medium text-gray-700 hidden xl:block">{{ AuthenticationService.userValue?.name }}</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</nav>
<!-- Mobile menu -->
@if (menuVisible) {
  <div id="mobile-menu"
       class="lg:hidden fixed inset-0 top-14 z-[100] bg-white/95 backdrop-blur-xl animate-in fade-in slide-in-from-right duration-300 overflow-y-auto">
    <div class="p-4 space-y-2">
      @for (navbarItem of NavbarService.getAvailable(); track navbarItem.title) {
        <app-navbar-office-item (click)="select(navbarItem)" [mobile]="true" [navbarItem]="navbarItem"/>
      }

      <div class="pt-2 mt-2 border-t border-gray-100">
        <!--
          `gap-3`, not `space-x-3`: the latter is a `margin-left` on every child but the first, which beats the
          switch button's `ml-auto` and parks it next to the name instead of at the right edge.
        -->
        <div class="flex items-center p-3 gap-3">
          <div class="h-10 w-10 flex-shrink-0 rounded-full bg-gray-100 flex items-center justify-center">
            <svg class="h-6 w-6 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
              <path clip-rule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" fill-rule="evenodd"/>
            </svg>
          </div>
          <!-- min-w-0 + truncate: a long name must shorten rather than push the switch button off a narrow phone. -->
          <div class="min-w-0">
            <div class="text-sm font-semibold text-gray-900 truncate">{{ AuthenticationService.userValue?.name }}</div>
            @if (roleLabel) {
              <div class="text-xs text-gray-500 truncate">{{ roleLabel }}</div>
            }
          </div>
          <!--
            Bug #405: the desktop "Benutzer wechseln" arrow lives in the `navbar-user` tab, which is only
            reachable through a `hidden lg:flex` profile button — so below `lg` the session switch had no entry
            point at all. Same guard as the desktop one (`navbar-user.component.html`): switching mid-
            impersonation would orphan the audit session, so the button is not rendered then.
          -->
          @if (!AuthenticationService.isImpersonating) {
            <button (click)="switchUser()" aria-label="Benutzer wechseln"
                    class="ml-auto flex-shrink-0 h-11 w-11 flex items-center justify-center p-2 text-gray-500 hover:bg-gray-100/80 hover:text-gray-900 rounded-lg transition-colors"
                    title="Benutzer wechseln" type="button">
              <svg aria-hidden="true" class="h-6 w-6" fill="currentColor" viewBox="0 0 24 24"
                   xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M15.75 13.997a2.25 2.25 0 0 1 2.25 2.25v.918c0 .285-.045.567-.13.836h-2.035a1.75 1.75 0 0 0-2.822-1.98l-2.5 2.499a1.75 1.75 0 0 0 0 2.476l.935.933q-.697.07-1.452.07c-3.42 0-5.943-1.073-7.486-3.237A2.75 2.75 0 0 1 2 17.166v-.92a2.25 2.25 0 0 1 2.249-2.249zM9.997 2.002a5 5 0 1 1 0 10a5 5 0 0 1 0-10m4.784 15.782a.75.75 0 0 0-1.06-1.06l-2.5 2.5a.75.75 0 0 0 0 1.06l2.5 2.5a.75.75 0 0 0 1.06-1.061l-1.22-1.22h6.88l-1.22 1.22a.75.75 0 0 0 1.06 1.06l2.5-2.498a.75.75 0 0 0 0-1.06l-2.5-2.502a.75.75 0 1 0-1.06 1.06l1.218 1.22h-6.877z"/>
              </svg>
            </button>
          }
        </div>
        <div class="grid grid-cols-2 gap-2 mt-2">
          <a (click)="menuClose()" [routerLink]="['/' + Routes.AuthenticationProfile]"
             class="flex items-center justify-center p-2 rounded-lg bg-gray-50 text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors">
            Profil
          </a>
          @if (!AuthenticationService.isImpersonating) {
            <!-- red-700 on red-50 = 5.9:1. red-600 measured 4.41:1 at 14px/500 — just under the 4.5:1 axe floor. -->
            <a (click)="AuthenticationService.logout()"
               class="flex items-center justify-center p-2 rounded-lg bg-red-50 text-sm font-medium text-red-700 hover:bg-red-100 transition-colors">
              Abmelden
            </a>
          }
        </div>
      </div>
    </div>
  </div>
}

<div class="w-full fixed top-14 inset-x-0 z-[49]">
  <div
    [ngClass]="{'opacity-100 translate-y-0': NavbarService.hasExpanded(), 'opacity-0 -translate-y-2': !NavbarService.hasExpanded()}"
    class="w-full absolute h-24 bg-white/90 backdrop-blur-md border-b border-gray-200/50 transition-all duration-300 ease-out shadow-sm pointer-events-none"></div>

  <div class="relative z-10">
    <app-navbar-office-item *ngFor="let navbarItem of NavbarService.getAvailable(false)" [actions]="true"
                            [navbarItem]="navbarItem">
      <ng-container *ngComponentOutlet="navbarItem.component"></ng-container>
    </app-navbar-office-item>
  </div>
</div>
